import { LocalityFilter } from 'rentalui-finance';
import { useLocation as useRecoveryLocation } from 'react-router-dom';
import { client as recoveryClient } from '../api';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Tabs, Tab } from 'react-bootstrap';
import ResponsiveTable from '../components/ResponsiveTable';
import MySubscriptionPanel from '../components/MySubscriptionPanel';
import TrialNudgeModal from '../components/TrialNudgeModal';
import { updateProperty, getMyProperties, getMyVisits, imageUrl, getOwnerFinanceSurface } from '../api';
import { VisitStatus, VisitActions } from '../components/VisitActions';
import { VisitDetailsModal, usePagedArray, Pager } from 'rentalui-finance';
import OwnerAgentsPanel from '../components/OwnerAgentsPanel';
import OwnerPayablesPanel from '../components/OwnerPayablesPanel';
import InvoicesPanel from '../components/InvoicesPanel';
import EmailsPanel from '../components/EmailsPanel';
import { VISIT_RANGE_OPTIONS, visitInRange } from '../components/visitRanges';

// The structured site address is stored as flat attributes.site* keys - used to hint the agent
// picker toward whoever covers the property's area.
function siteAreaHint(property) {
  const a = property?.attributes || {};
  return a.siteArea || a.siteCity || property?.location || '';
}

export default function OwnerDashboard() {
  const [locality, setLocality] = useState('');
  const recoveryLocation = useRecoveryLocation();
  useEffect(() => {
    const params = new URLSearchParams(recoveryLocation.search);
    const id = params.get('recoveryCase');
    const bookingId = params.get('bookingId');
    if (!id && !bookingId) return undefined;
    let alive = true;
    recoveryClient.get(bookingId ? `/api/order-attention/orders/real_estate/${encodeURIComponent(bookingId)}` : `/api/order-attention/${id}/details`).then(response => {
      if (bookingId) response = { data: { data: { order: response.data.data } } };
      if (alive && response.data.data.order) setStory6Visit(response.data.data.order);
    }).catch(() => { if (alive) setError('Unable to open the requested order. Return to its recovery page and retry.'); });
    return () => { alive = false; };
  }, [recoveryLocation.search]);

  const [activeTab, setActiveTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visitsForPropertyId, setVisitsForPropertyId] = useState(null); // story 6
  const [story6Visit, setStory6Visit] = useState(null);
  const [todayRange, setTodayRange] = useState('today'); // "Today's Visits" tab range filter
  const [financeSurface, setFinanceSurface] = useState({ visit_fees: false, delivery_payments: false });

  // Promise.allSettled, not Promise.all - a failure on one call (e.g. visits) shouldn't hide
  // data that did load successfully from the other (e.g. properties). Previously one endpoint
  // erroring meant the whole dashboard showed a blank "Could not load" message even though the
  // other half of the data was available.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [propertiesResult, visitsResult] = await Promise.allSettled([getMyProperties(), getMyVisits()]);
    if (propertiesResult.status === 'fulfilled') setProperties(propertiesResult.value);
    if (visitsResult.status === 'fulfilled') setVisits(visitsResult.value);

    const failed = [];
    if (propertiesResult.status === 'rejected') failed.push('properties');
    if (visitsResult.status === 'rejected') failed.push('visits');
    if (failed.length > 0) {
      setError(`Could not load your ${failed.join(' or ')}. Showing what did load.`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    getOwnerFinanceSurface().then(setFinanceSurface).catch(() => {});
  }, []);

  // fix_issues.md #6 - a listing is taken off the public site by disabling it, not by deleting it
  // outright; disabling and re-enabling both just PATCH status (see common/catalogRepository.js's
  // list() for where 'disabled' is actually excluded from public browsing).
  const handleToggleStatus = async (property) => {
    const nextStatus = property.status === 'disabled' ? 'available' : 'disabled';
    await updateProperty(property._id, { status: nextStatus });
    load();
  };

  const propertyById = (propertyId) => properties.find((p) => p._id === propertyId);
  const propertyName = (propertyId) => propertyById(propertyId)?.name || 'Property';
  const visitsForProperty = (propertyId) => visits.filter((v) => v.itemId === propertyId);
  const areaHintFor = (visit) => siteAreaHint(propertyById(visit.itemId));

  // Visit status + step-actions now live in the shared VisitActions component (the website owner's
  // dashboard drives visits with the exact same controls).
  const renderVisitStatus = (visit, compact = false) => <VisitStatus visit={visit} compact={compact} />;
  const renderVisitActions = (visit) => (
    <VisitActions visit={visit} areaHint={areaHintFor(visit)} onChanged={load} />
  );

  const todaysVisits = visits.filter((v) => visitInRange(v, todayRange));

  const localityOptions = [...new Set(properties.map(item => item.location).filter(Boolean))];
  const matchesLocality = row => !locality || String(row.itemLocation || row.location || properties.find(item => String(item._id) === String(row.itemId))?.location || '').toLowerCase().includes(locality.toLowerCase());
  const propertiesPaged = usePagedArray(properties.filter(matchesLocality), { resetKey: locality });
  const todaysVisitsPaged = usePagedArray(todaysVisits.filter(matchesLocality), { resetKey: `${todayRange}:${locality}` });
  const visitsPaged = usePagedArray(visits.filter(matchesLocality), { resetKey: locality });

  if (loading) return <Spinner animation="border" role="status" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Owner dashboard</h3>
        <Button as={Link} to="/owner/properties/new">
          + List a property
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {visits.some((visit) => visit.contactAccess === 'restricted') && (
        <Alert variant="info">
          Some visitor contact details are unavailable for your account. Review your subscription or contact support if you believe you already have access.
          {' '}<Button variant="link" className="p-0" onClick={() => setActiveTab('subscription')}>Review subscription</Button>
        </Alert>
      )}

      {['items', 'properties', 'bookings', 'completed', 'cancelled', 'deliveries', 'today', 'visits'].includes(activeTab) && <LocalityFilter value={locality} options={localityOptions} onChange={setLocality} />}
      <TrialNudgeModal listingCount={properties.length} onActivated={load} />

      <Tabs activeKey={activeTab} onSelect={setActiveTab} defaultActiveKey="properties" className="mb-3">
        <Tab eventKey="properties" title={`My Properties (${properties.length})`}>
          {propertiesPaged.total === 0 ? (
            <Alert variant="light">You haven't listed any properties yet.</Alert>
          ) : (
            <>
            <div className="row g-3">
              {propertiesPaged.rows.map((property) => (
                <div className="col-md-4" key={property._id}>
                  <Card className="h-100">
                    {property.images?.[0] && (
                      <Card.Img variant="top" src={imageUrl(property.images[0])} style={{ height: 140, objectFit: 'cover' }} />
                    )}
                    <Card.Body>
                      <Card.Title as="h6">{property.name}</Card.Title>
                      <Card.Text className="text-muted small">
                        {property.location} &middot; ₹{property.price}/{property.priceUnit}
                      </Card.Text>
                      <Badge bg={property.status === 'available' ? 'success' : 'secondary'} className="mb-2">
                        {property.status}
                      </Badge>
                      <div className="d-flex flex-wrap gap-2">
                        <Button size="sm" variant="outline-secondary" as={Link} to={`/owner/properties/${property._id}/edit`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-primary" onClick={() => setVisitsForPropertyId(property._id)}>
                          Visits ({visitsForProperty(property._id).length})
                        </Button>
                        <Button
                          size="sm"
                          variant={property.status === 'disabled' ? 'outline-success' : 'outline-danger'}
                          onClick={() => handleToggleStatus(property)}
                        >
                          {property.status === 'disabled' ? 'Enable' : 'Disable'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
            <Pager {...propertiesPaged} />
            </>
          )}
        </Tab>

        {/* Today's schedule - same "what's happening now" focus as jewelry's delivery "My schedule". */}
        <Tab eventKey="today" title={`Today's Visits (${todaysVisits.length})`}>
          <Form.Select
            size="sm" style={{ maxWidth: 180 }} className="mb-3"
            value={todayRange} onChange={(e) => setTodayRange(e.target.value)}
            aria-label="Visit date range"
          >
            {VISIT_RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </Form.Select>
          {todaysVisitsPaged.total === 0 ? (
            <Alert variant="light">No visits in this range.</Alert>
          ) : (
            <>
            <ResponsiveTable
              cardsBelow="xl"
              rows={todaysVisitsPaged.rows}
              rowKey={(v) => v._id}
              empty="No visits in this range."
              columns={[
                { key: 'property', header: 'Property', card: 'title', cell: (visit) => propertyName(visit.itemId) },
                {
                  key: 'requestedBy', header: 'Requested by', card: 'subtitle',
                  cell: (visit) => (<>{visit.customerName}<div className="small text-muted">{visit.contactAccess === 'restricted' ? 'Contact details unavailable' : [visit.email, visit.phoneNumber].filter(Boolean).join(' ? ')}</div></>)
                },
                { key: 'when', header: 'When', cell: (visit) => new Date(visit.deliveryAssignment?.scheduledAt || visit.startDate).toLocaleString() },
                { key: 'status', header: 'Status', card: 'badge', cell: (visit) => renderVisitStatus(visit) }
              ]}
              actions={(visit) => renderVisitActions(visit)}
            />
            <Pager {...todaysVisitsPaged} />
            </>
          )}
        </Tab>

        <Tab eventKey="visits" title={`Visit Requests (${visits.length})`}>
          {visitsPaged.total === 0 ? (
            <Alert variant="light">No visit requests yet.</Alert>
          ) : (
            <>
              <ResponsiveTable
                cardsBelow="xl"
                rows={visitsPaged.rows}
                rowKey={(v) => v._id}
                empty="No visit requests yet."
                columns={[
                  { key: 'property', header: 'Property', card: 'title', cell: (visit) => propertyName(visit.itemId) },
                  {
                    key: 'requestedBy', header: 'Requested by', card: 'subtitle',
                    cell: (visit) => (<>{visit.customerName}<br /><small className="text-muted">{visit.contactAccess === 'restricted' ? 'Contact details unavailable' : [visit.email, visit.phoneNumber].filter(Boolean).join(' ? ')}</small></>)
                  },
                  { key: 'date', header: 'Date', cell: (visit) => new Date(visit.startDate).toLocaleString() },
                  { key: 'notes', header: 'Notes', hideEmpty: true, cell: (visit) => visit.notes },
                  { key: 'status', header: 'Status', card: 'badge', cell: (visit) => renderVisitStatus(visit) }
                ]}
                actions={(visit) => renderVisitActions(visit)}
              />
              <Pager {...visitsPaged} />
            </>
          )}
        </Tab>

        {/* 03_requirements_missing.md story 3 - request + track site-visit agents. */}
        <Tab eventKey="agents" title="Agents" mountOnEnter unmountOnExit>
          <OwnerAgentsPanel />
        </Tab>

        {(financeSurface.visit_fees || financeSurface.delivery_payments) && (
          <Tab eventKey="agent-payments" title="Agent payments" mountOnEnter unmountOnExit>
            <OwnerPayablesPanel />
          </Tab>
        )}

        {/* 08_owner_invoice_pdf_export.md - manual invoice generation + PDF export, real_estate only. */}
        <Tab eventKey="invoices" title="Invoices" mountOnEnter unmountOnExit>
          <InvoicesPanel />
        </Tab>
        <Tab eventKey="subscription" title="Subscription" mountOnEnter unmountOnExit>
          <MySubscriptionPanel />
        </Tab>
        <Tab eventKey="emails" title="Emails" mountOnEnter unmountOnExit>
          <EmailsPanel scope="owner" />
        </Tab>
      </Tabs>

      {/* story 6 - every visit booked on one listing, in one place. */}
      <Modal show={!!visitsForPropertyId} onHide={() => setVisitsForPropertyId(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h5">Visits — {propertyName(visitsForPropertyId)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {visitsForProperty(visitsForPropertyId).length === 0 ? (
            <Alert variant="light" className="mb-0">No visits booked on this property yet.</Alert>
          ) : (
            <ResponsiveTable
              cardsBelow="xl"
              rows={visitsForProperty(visitsForPropertyId)}
              rowKey={(v) => v._id}
              empty="No visits booked on this property yet."
              columns={[
                { key: 'when', header: 'When', card: 'title', cell: (visit) => new Date(visit.startDate).toLocaleString() },
                {
                  key: 'visitor', header: 'Visitor', card: 'subtitle',
                  cell: (visit) => (<>{visit.customerName}<div className="small text-muted">{visit.email} · {visit.phoneNumber}</div></>)
                },
                { key: 'status', header: 'Status', card: 'badge', cell: (visit) => renderVisitStatus(visit) }
              ]}
              actions={(visit) => [
                { label: 'Details', variant: 'outline-secondary', onClick: () => setStory6Visit(visit) }
              ]}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* v6 finance_module (Part C) — the shared single-visit popup, with the same VisitActions
          the owner has elsewhere. */}
      <VisitDetailsModal
        visit={story6Visit}
        show={!!story6Visit}
        onHide={() => setStory6Visit(null)}
        renderActions={(visit) => (
          <VisitActions
            visit={visit}
            areaHint={areaHintFor(visit)}
            onChanged={() => { load(); setStory6Visit(null); }}
          />
        )}
      />
    </div>
  );
}
